import axios from 'axios';
import { redirectToLogin, getLastTenantSlug } from '../utils/sessionNavigation.js';

/**
 * Base URL:
 * - Default: `/api` (mismo origen) → funciona en dev con proxy Vite y en LAN (móvil).
 * - Override: VITE_API_BASE_URL (p. ej. `http://localhost:3001` o `https://alinea-demo.duckdns.org`).
 */
const RAW_BASE = (import.meta.env.VITE_API_BASE_URL || '').trim();
const API_BASE_URL = RAW_BASE
    ? RAW_BASE.endsWith('/api')
        ? RAW_BASE
        : `${RAW_BASE}/api`
    : '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/** Auth: 401/403 son respuestas de negocio (código/contraseña), no excepciones de red. */
const authValidateStatus = (status) => status >= 200 && status < 500;

// Interceptor para añadir el token JWT en cada petición
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('alinea_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        try {
            const user = JSON.parse(localStorage.getItem('alinea_user') || 'null');
            if (user?.rol === 'superadmin') {
                const tid = localStorage.getItem('alinea_superadmin_tenant_id');
                if (tid) {
                    config.params = { ...config.params, tenant_id: tid };
                }
            }
        } catch {
            /* ignore */
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor para manejar errores globales (ej. 401 Unauthorized)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = String(error.config?.url || '');
            const isAuthRoute =
                url.includes('/auth/login') ||
                url.includes('/auth/check-code') ||
                url.includes('/auth/me');
            if (!isAuthRoute && !window.location.pathname.startsWith('/login')) {
                let sessionUser = null;
                try {
                    sessionUser = JSON.parse(localStorage.getItem('alinea_user') || 'null');
                } catch {
                    sessionUser = null;
                }
                localStorage.removeItem('alinea_token');
                localStorage.removeItem('alinea_user');
                redirectToLogin({
                    user: sessionUser,
                    fallbackSlug: getLastTenantSlug(),
                });
            }
        }
        return Promise.reject(error);
    }
);

// Helpers para los endpoints de los módulos
export const authAPI = {
    checkCode: (codigo, tenant_slug) => {
        const body = { codigo_empleado: codigo };
        if (tenant_slug) body.tenant_slug = tenant_slug;
        return api.post('/auth/check-code', body, { validateStatus: authValidateStatus });
    },
    login: (codigo, contrasena, tenant_slug) => {
        const body = { codigo_empleado: codigo };
        if (contrasena != null && String(contrasena).length > 0) {
            body.contrasena = contrasena;
        }
        if (tenant_slug) {
            body.tenant_slug = tenant_slug;
        }
        return api.post('/auth/login', body, { validateStatus: authValidateStatus });
    },
    getMe: () => api.get('/auth/me'),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    changePassword: (data) => api.post('/auth/change-password', data),
};

export const employeeAPI = {
    getList: () => api.get('/employees'),
    getDetalle: (id) => api.get(`/employees/${id}`),
    bulkUpload: (formData) => api.post('/employees/bulk-upload', formData),
};

export const diagnosticoAPI = {
    submitCheckin: (data) => api.post('/diagnostico/checkin', data),
    getHistorial: () => api.get('/diagnostico/mi-historial'),
    submitEncuesta: (data) => api.post('/diagnostico/encuesta-semanal', data),
    getEquipo: (supervisorId) => api.get(`/diagnostico/equipo/${supervisorId}`),
};

export const epaAPI = {
    submitSomatico: (data) => api.post('/epa/checkin-somatico', data),
    reportarIncidente: (data) => api.post('/epa/incidente', data),
    getLecciones: () => api.get('/epa/lecciones'),
};

export const alertsAPI = {
    getActive: (params) => api.get('/alertas/activas', { params }),
    getMyAlerts: () => api.get('/alertas/mis-alertas'),
    acknowledge: (id) => api.patch(`/alertas/${id}/acknowledge`),
    intervene: (id, data) => api.post(`/alertas/${id}/intervene`, data),
    close: (id, data) => api.post(`/alertas/${id}/cerrar`, data),
};

export const coachAPI = {
    getPerfil: () => api.get('/coach/perfil-lider'),
    getGuias: () => api.get('/coach/guias'),
    getRecomendaciones: (id) => api.get(`/coach/recomendaciones/${id}`),
};

export const cidAPI = {
    calculateRenuncia: (data) => api.post('/cid/calcular-renuncia', data),
    getResumenAnual: (year) => api.get('/cid/resumen-anual', { params: { year } }),
    getResumenPorArea: (areaId, year) => api.get(`/cid/por-area/${areaId}`, { params: { year } }),
};

export const adminAPI = {
    getStats: () => api.get('/admin/stats'),
    getHeatmap: () => api.get('/admin/heatmap'),
    getAreaSummary: (areaId) => api.get(`/admin/areas/${areaId}/summary`),
    getSupervisorsMap: () => api.get('/admin/supervisors-map'),
    getTrends: () => api.get('/admin/trends'),
    getIndicators: () => api.get('/admin/indicators'),
};

export const messageAPI = {
    getHoy: () => api.get('/messages/hoy'),
    getHistory: () => api.get('/messages/history'),
    submitFeedback: (id, feedback) => api.post(`/messages/${id}/feedback`, { feedback }),
    // Admin
    getAdminMessages: () => api.get('/messages'),
    createMessage: (data) => api.post('/messages', data),
    updateMessage: (id, data) => api.put(`/messages/${id}`, data),
    deleteMessage: (id) => api.delete(`/messages/${id}`),
    getAdminFeedback: () => api.get('/messages/admin/feedback'),
};

export const rolesAPI = {
    getPermissions: () => api.get('/roles/permissions'),
    getRoles: () => api.get('/roles'),
    updatePermissions: (data) => api.post('/roles/permissions', data),
};

export default api;
